/**
 * The resume path on the turn pipeline (HIG-5970): connect to `/subscribe`
 * with backoff, drain through the shared pipeline, and settle the per-chat
 * streaming markers. Runs inside a turn-registry turn; all shared-state
 * cleanup gates on `isOwner` so a superseded run can't clobber its successor.
 */
import { Result, ResultAsync, err, ok } from 'neverthrow'
import { initialTurnState, type MessageSeed } from './accumulator'
import { backendTurnError, interruptReasonFrom, rayIdFromHeaders, type ChatTurnError } from './errors'
import { waitForBackoff } from './retry'
import type { EngineDeps } from './services'
import { createStreamAttempt, monitorStreamStalls } from './sse'
import { runTurnStream, type ResumeCompletion, type TurnCheckpoint } from './turn-pipeline'
import { describeTurnErrorForTrace, settledTrace, type TurnScope } from './turn-trace'

const MAX_RECONNECT_ATTEMPTS = 3
const RECONNECT_BASE_MS = 500
const RECONNECT_MAX_MS = 10_000

export type ResumeParams = {
  readonly chatId: string
  /** Identity of this engine turn for telemetry; the hook mints it. */
  readonly turnId: string
  /** Store content to seed a message's fold from — see TurnPipelineDeps.seedFor. */
  readonly seedFor: (messageId: string) => MessageSeed | undefined
  /** First-failure tap for UI surfacing (turnErrorAtom + toast); called at most once. */
  readonly onTurnError: (error: ChatTurnError) => void
  /** Registry ownership check; false once a newer turn supersedes this one. */
  readonly isOwner: () => boolean
  readonly reconcileMessages?: () => Promise<void>
  readonly lastEventId?: number
  readonly onLastEventId?: (lastEventId: number | undefined) => void
}

/**
 * Resume a chat's in-progress turn. Resolves `completed`/`interrupted` on the
 * Ok side; the first failure lands on the Err channel *and* is surfaced at
 * event time through `onTurnError` + telemetry.
 */
export function resumeTurn(
  deps: EngineDeps,
  params: ResumeParams,
  signal: AbortSignal,
): ResultAsync<ResumeCompletion, ChatTurnError> {
  const { chatId } = params
  const ctx: TurnScope = { phase: 'resume', chatId, turnId: params.turnId }

  const surface = (error: ChatTurnError) => {
    deps.telemetry.report(error, { turnId: ctx.turnId })
    params.onTurnError(error)
  }
  const publishCursor = (lastEventId: number | undefined) => {
    if (params.isOwner()) params.onLastEventId?.(lastEventId)
  }

  const finish = async (
    result: Result<ResumeCompletion, ChatTurnError>,
    reconcile = false,
  ): Promise<Result<ResumeCompletion, ChatTurnError>> => {
    // A client abort leaves the chat in the streaming map: the backend stream
    // is still running, and a future drawer open rebuilds state via the
    // verify + re-subscribe. Completion and failure settle it — but only
    // while this turn still owns the chat.
    const interrupted = result.isOk() && result.value._tag === 'interrupted'
    const ownsTurn = params.isOwner()
    if (!interrupted && ownsTurn) {
      deps.streaming.end(chatId)
    }
    if (reconcile && !interrupted && ownsTurn) await params.reconcileMessages?.()
    return result
  }

  const run = async (): Promise<Result<ResumeCompletion, ChatTurnError>> => {
    deps.streaming.begin(chatId)
    deps.telemetry.trace({ kind: 'resume:start', chatId, turnId: ctx.turnId, lastEventId: params.lastEventId })

    const checkpoint: TurnCheckpoint = {
      state: initialTurnState(chatId),
      lastEventId: params.lastEventId,
      terminal: false,
    }
    let reconnectAttempts = 0

    while (true) {
      const attempt = createStreamAttempt(signal)
      const lastEventIdBeforeAttempt = checkpoint.lastEventId
      let disposeMonitor: (() => void) | undefined
      let retryableFailure: ChatTurnError | undefined
      let stalled = false

      try {
        const connected = await deps.api.subscribeToTurnStream(
          chatId,
          attempt.controller.signal,
          checkpoint.lastEventId,
        )
        if (connected.isErr()) {
          if (signal.aborted) {
            return await finish(ok({ _tag: 'interrupted', reason: interruptReasonFrom(signal) }))
          }
          deps.telemetry.trace({
            kind: 'connect:failed',
            ...ctx,
            attempt: reconnectAttempts + 1,
            cause: describeTurnErrorForTrace(connected.error),
          })
          retryableFailure = connected.error
        } else {
          const response = connected.value
          deps.telemetry.trace({
            kind: 'connect:response',
            ...ctx,
            attempt: reconnectAttempts + 1,
            status: response.status,
            rayId: rayIdFromHeaders(response.headers),
          })
          if (response.status === 204) {
            checkpoint.terminal = true
            publishCursor(undefined)
            return await finish(ok({ _tag: 'completed', state: checkpoint.state }), true)
          }

          if (!response.ok) {
            const failure = backendTurnError(ctx, {
              kind: 'error-response',
              message: `Failed to reconnect to the chat stream (HTTP ${response.status}).`,
              rayId: rayIdFromHeaders(response.headers),
            })
            if (response.status >= 500) retryableFailure = failure
            else {
              surface(failure)
              return await finish(err(failure))
            }
          } else {
            deps.streaming.markStreaming(chatId)
            const monitored = monitorStreamStalls(response, () => {
              stalled = true
              attempt.controller.abort('stream-stalled')
            })
            disposeMonitor = monitored.dispose

            const outcome = await runTurnStream(
              {
                sink: deps.sink,
                telemetry: deps.telemetry,
                seedFor: params.seedFor,
                onTurnError: (error) => {
                  if (error._tag !== 'SseConnectionError') surface(error)
                },
                // Per frame, so a stream taking over mid-attempt reads this
                // run's real progress rather than its last attempt boundary.
                onLastEventId: publishCursor,
              },
              ctx,
              monitored.response,
              attempt.controller.signal,
              checkpoint.state,
              checkpoint,
            )

            if (checkpoint.terminal) publishCursor(undefined)
            if (outcome.isErr() && outcome.error._tag !== 'SseConnectionError') {
              return await finish(outcome)
            }
            if (checkpoint.terminal) {
              return await finish(ok({ _tag: 'completed', state: checkpoint.state }))
            }
            if (signal.aborted) {
              return await finish(ok({ _tag: 'interrupted', reason: interruptReasonFrom(signal) }))
            }
            retryableFailure =
              outcome.isErr() && outcome.error._tag === 'SseConnectionError'
                ? outcome.error
                : {
                    _tag: 'SseConnectionError',
                    phase: 'resume',
                    chatId,
                    cause: new Error(stalled ? 'SSE stream stalled' : 'SSE stream closed before done'),
                  }
          }
        }
      } finally {
        disposeMonitor?.()
        attempt.dispose()
      }

      if (checkpoint.lastEventId !== lastEventIdBeforeAttempt) reconnectAttempts = 0
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        surface(retryableFailure!)
        return await finish(err(retryableFailure!))
      }

      reconnectAttempts += 1
      deps.telemetry.trace({
        kind: 'stream:reconnect',
        ...ctx,
        attempt: reconnectAttempts,
        cause: describeTurnErrorForTrace(retryableFailure!),
      })
      await waitForBackoff(reconnectAttempts, {
        baseMs: RECONNECT_BASE_MS,
        maxMs: RECONNECT_MAX_MS,
        signal,
      })
      if (signal.aborted) {
        return await finish(ok({ _tag: 'interrupted', reason: interruptReasonFrom(signal) }))
      }
    }
  }

  const startedAt = Date.now()
  return new ResultAsync(
    run().then((result) => {
      deps.telemetry.trace(settledTrace(ctx, result, Date.now() - startedAt))
      return result
    }),
  )
}
